import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Search, Filter, RefreshCw, Eye, MessageSquare, Clock, CheckCircle, XCircle } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import ticketService, { TicketStatus, TicketPriority } from '../../services/ticketService';
import { TicketStatusPersian, TicketPriorityPersian, TicketStatusBadge, TicketPriorityBadge } from '../../constants/ticketEnums';
import toast from 'react-hot-toast';

const TicketsContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.gradient || 'linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%)'};
  }
`;

const StatValue = styled.div`
  color: #ffffff;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: #b8b8d1;
  font-size: 14px;
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 5px;
  flex-wrap: wrap;
`;

const FilterTab = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)' : 'transparent'};
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  color: ${props => props.active ? 'white' : '#b8b8d1'};
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)' : 'rgba(0, 212, 255, 0.1)'};
    color: ${props => props.active ? 'white' : '#00d4ff'};
  }
`;

const TicketsTable = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 150px 120px 120px 150px 100px;
  gap: 20px;
  padding: 20px;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
  border-bottom: 1px solid #2d2d44;
  font-weight: 600;
  color: #ffffff;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 80px 1fr 150px 120px 120px 150px 100px;
  gap: 20px;
  padding: 20px;
  border-bottom: 1px solid #2d2d44;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(90deg, rgba(0, 212, 255, 0.05) 0%, transparent 100%);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const TicketId = styled.div`
  color: #00d4ff;
  font-weight: 600;
  font-size: 14px;
`;

const TicketTitle = styled.div`
  color: #ffffff;
  font-weight: 500;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const PlayerName = styled.div`
  color: #b8b8d1;
  font-size: 14px;
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'open': return 'linear-gradient(135deg, #ffd93d 0%, #ff6b6b 100%)';
      case 'in_progress': return 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)';
      case 'closed': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #4a4a6a 100%)';
    }
  }};
  color: white;
`;

const PriorityBadge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.priority) {
      case 'low': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'medium': return 'linear-gradient(135deg, #ffd93d 0%, #ff6b6b 100%)';
      case 'high': return 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #4a4a6a 100%)';
    }
  }};
  color: white;
`;

const LastUpdate = styled.div`
  color: #6c6c8a;
  font-size: 12px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.4);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const SupportTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, priorityFilter]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (priorityFilter !== 'all') filters.priority = priorityFilter;
      if (searchTerm) filters.search = searchTerm;
      
      const data = await ticketService.getTickets(filters);
      setTickets(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('خطا در بارگذاری تیکت‌ها');
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTickets();
  };

  const getFilteredTickets = () => {
    let filtered = tickets;

    if (statusFilter !== 'all') {
      if (statusFilter === 'open' || statusFilter === TicketStatus.Open) {
        filtered = filtered.filter(ticket => 
          ticket.status === 'open' || ticket.status === TicketStatus.Open
        );
      } else if (statusFilter === 'in_progress' || statusFilter === TicketStatus.InProgress) {
        filtered = filtered.filter(ticket => 
          ticket.status === 'in_progress' || ticket.status === TicketStatus.InProgress
        );
      } else if (statusFilter === 'closed' || statusFilter === TicketStatus.Closed) {
        filtered = filtered.filter(ticket => 
          ticket.status === 'closed' || ticket.status === TicketStatus.Closed
        );
      }
    }

    if (priorityFilter !== 'all') {
      if (priorityFilter === 'low' || priorityFilter === TicketPriority.Low) {
        filtered = filtered.filter(ticket => 
          ticket.priority === 'low' || ticket.priority === TicketPriority.Low
        );
      } else if (priorityFilter === 'medium' || priorityFilter === TicketPriority.Medium) {
        filtered = filtered.filter(ticket => 
          ticket.priority === 'medium' || ticket.priority === TicketPriority.Medium
        );
      } else if (priorityFilter === 'high' || priorityFilter === TicketPriority.High) {
        filtered = filtered.filter(ticket => 
          ticket.priority === 'high' || ticket.priority === TicketPriority.High
        );
      }
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(ticket => 
        ticket.title?.toLowerCase().includes(searchLower) ||
        ticket.playerUsername?.toLowerCase().includes(searchLower) ||
        ticket.id?.toString().includes(searchLower)
      );
    }

    return filtered;
  };

  const getStatusText = (status) => {
    if (typeof status === 'number') {
      return TicketStatusPersian[status] || `نامشخص (${status})`;
    }
    
    const stringToEnum = {
      'open': TicketStatus.Open,
      'in_progress': TicketStatus.InProgress,
      'closed': TicketStatus.Closed
    };
    const enumValue = stringToEnum[status];
    
    return enumValue !== undefined ? TicketStatusPersian[enumValue] : status;
  };

  const getPriorityText = (priority) => {
    if (typeof priority === 'number') {
      return TicketPriorityPersian[priority] || `نامشخص (${priority})`;
    }
    
    const stringToEnum = {
      'low': TicketPriority.Low,
      'medium': TicketPriority.Medium,
      'high': TicketPriority.High
    };
    const enumValue = stringToEnum[priority];
    
    return enumValue !== undefined ? TicketPriorityPersian[enumValue] : priority;
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return 'تاریخ نامعتبر';
      }
      return date.toLocaleDateString('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (dateError) {
      console.error('Error formatting date:', dateError);
      return 'تاریخ نامعتبر';
    }
  };

  const filteredTickets = getFilteredTickets();

  if (loading) {
    return (
      <TicketsContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ color: '#b8b8d1' }}>در حال بارگذاری تیکت‌ها...</div>
        </div>
      </TicketsContainer>
    );
  }

  return (
    <TicketsContainer>
      <Header>
        <Title>مدیریت تیکت‌های پشتیبانی</Title>
        <SearchContainer>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Input
              placeholder="جستجو در تیکت‌ها..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ minWidth: '250px' }}
            />
            <Button type="submit" variant="outline" icon={<Search size={16} />}>
              جستجو
            </Button>
          </form>
          <Button variant="outline" onClick={fetchTickets} icon={<RefreshCw size={16} />}>
            بروزرسانی
          </Button>
        </SearchContainer>
      </Header>

      <StatsGrid>
        <StatCard
          gradient="linear-gradient(90deg, #ffd93d 0%, #ff6b6b 100%)"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <StatValue>
            {tickets.filter(t => t.status === 'open' || t.status === TicketStatus.Open).length}
          </StatValue>
          <StatLabel>تیکت‌های باز</StatLabel>
        </StatCard>

        <StatCard
          gradient="linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%)"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <StatValue>
            {tickets.filter(t => t.status === 'in_progress' || t.status === TicketStatus.InProgress).length}
          </StatValue>
          <StatLabel>در حال بررسی</StatLabel>
        </StatCard>

        <StatCard
          gradient="linear-gradient(90deg, #4ecdc4 0%, #44a08d 100%)"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <StatValue>
            {tickets.filter(t => t.status === 'closed' || t.status === TicketStatus.Closed).length}
          </StatValue>
          <StatLabel>بسته شده</StatLabel>
        </StatCard>

        <StatCard
          gradient="linear-gradient(90deg, #ff6b6b 0%, #ff8e8e 100%)"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <StatValue>
            {tickets.filter(t => t.priority === 'high' || t.priority === TicketPriority.High).length}
          </StatValue>
          <StatLabel>اولویت بالا</StatLabel>
        </StatCard>
      </StatsGrid>

      <FilterTabs>
        <FilterTab 
          active={statusFilter === 'all'} 
          onClick={() => setStatusFilter('all')}
        >
          همه وضعیت‌ها
        </FilterTab>
        <FilterTab 
          active={statusFilter === 'open'} 
          onClick={() => setStatusFilter('open')}
        >
          باز
        </FilterTab>
        <FilterTab 
          active={statusFilter === 'in_progress'} 
          onClick={() => setStatusFilter('in_progress')}
        >
          در حال بررسی
        </FilterTab>
        <FilterTab 
          active={statusFilter === 'closed'} 
          onClick={() => setStatusFilter('closed')}
        >
          بسته
        </FilterTab>
      </FilterTabs>

      {filteredTickets.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🎫</EmptyIcon>
          <EmptyTitle>هیچ تیکتی یافت نشد</EmptyTitle>
          <EmptyDescription>
            {searchTerm ? 'نتیجه‌ای برای جستجوی شما یافت نشد' : 'هنوز تیکتی در سیستم ثبت نشده است'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <TicketsTable>
          <TableHeader>
            <div>شماره</div>
            <div>عنوان</div>
            <div>بازیکن</div>
            <div>وضعیت</div>
            <div>اولویت</div>
            <div>آخرین بروزرسانی</div>
            <div>عملیات</div>
          </TableHeader>
          
          {filteredTickets.map((ticket, index) => (
            <TableRow
              key={ticket.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <TicketId>#{ticket.id}</TicketId>
              <TicketTitle title={ticket.title}>
                {ticket.title || 'بدون عنوان'}
              </TicketTitle>
              <PlayerName>{ticket.playerUsername || 'نامشخص'}</PlayerName>
              <StatusBadge status={ticket.status}>
                {getStatusText(ticket.status)}
              </StatusBadge>
              <PriorityBadge priority={ticket.priority}>
                {getPriorityText(ticket.priority)}
              </PriorityBadge>
              <LastUpdate>{formatDate(ticket.updatedAt)}</LastUpdate>
              <Actions>
                <ActionButton onClick={() => navigate(`/support-tickets/${ticket.id}`)}>
                  <Eye size={14} />
                  مشاهده
                </ActionButton>
              </Actions>
            </TableRow>
          ))}
        </TicketsTable>
      )}
    </TicketsContainer>
  );
};

export default SupportTickets;
